import { invoke } from '@tauri-apps/api/core';
import { Unplug, FolderPlus, FilePlus, Upload } from 'lucide-react';
import FileEditModal from '../FileEditModal';
import { useFtpStore } from '../../store/ftpStore';
import TransfersStrip from './FtpTransfers';
import TransferConflictModal from './TransferConflictModal';
import { resolveRemotePath } from '../../utils/remotePath';
import { joinRemote, parentRemote } from './ftpViewHelpers';
import { NameModal } from './NameModal';
import { ExportArchiveModal } from './ExportArchiveModal';
import { RemoteComparePicker } from './RemoteComparePicker';
import { ZipInstallModal } from './ZipInstallModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { PermModal } from './PermModal';
import { FtpPane } from './FtpPane';

/** FtpView's connected layout: the two panes, the transfers strip and every modal. */
export function renderFtpConnected({
  t, remotePath, activeConnId, activeConn, remoteEntries, remoteNamesAt, remoteLoading, remoteError,
  remoteSelection, localPath, localEntries, localLoading, localError, localSelection, nameModal,
  setNameModal, editFile, setEditFile, permEntry, setPermEntry, confirmDelete, setConfirmDelete,
  zipHelp, setZipHelp, comparePick, setComparePick, exportSources, setExportSources, notice,
  showNotice, startPathUpload, startDownload, conflictQueue, answerConflict, transferBatch,
  pickUploadDialog, doArchive, moveEntriesTo, makeRowDropHandler, makeDropHandler, buildRemoteMenu,
  buildRemoteBackgroundMenu, buildLocalBackgroundMenu, buildLocalMenu, doDelete,
}) {
  /* ── Connected: dual panes ───────────────────────────────────────────── */

  const store = useFtpStore.getState();

  return (
    <div className="ftp-view ftp-view--connected">
      <div className="ftp-panes">
        <FtpPane
          side="local"
          title={t('ftp.localPane')}
          path={localPath}
          entries={localEntries}
          loading={localLoading}
          error={localError}
          selection={localSelection}
          setSelection={store.setLocalSelection}
          navigate={(p) => store.listLocal(p).catch(() => {})}
          refresh={() => store.listLocal(localPath || null).catch(() => {})}
          toolbarExtras={[]}
          onEntryOpen={(entry) => {
            if (entry.isDir) store.listLocal(entry.path).catch(() => {});
            else {
              transferBatch('upload', [
                { name: entry.name, start: (meta) => startPathUpload(entry.path, entry.name, undefined, meta) },
              ]);
            }
          }}
          buildMenuItems={buildLocalMenu}
          buildBackgroundMenuItems={buildLocalBackgroundMenu}
          onDropPane={makeDropHandler('local')}
          confirmBar={null}
          t={t}
        />
        <FtpPane
          side="remote"
          title={`${t('ftp.remotePane')} · ${activeConn.name}`}
          path={remotePath}
          entries={remoteEntries}
          loading={remoteLoading}
          error={remoteError}
          selection={remoteSelection}
          setSelection={store.setRemoteSelection}
          navigate={(p) => store.listRemote(p).catch(() => {})}
          refresh={() => store.listRemote(remotePath).catch(() => {})}
          toolbarExtras={[
            {
              id: 'new-folder',
              Icon: FolderPlus,
              title: t('ftp.newFolder'),
              onClick: () => setNameModal({ mode: 'mkdir' }),
            },
            {
              id: 'new-file',
              Icon: FilePlus,
              title: t('ftp.newFile'),
              onClick: () => setNameModal({ mode: 'newfile' }),
            },
            {
              id: 'upload',
              Icon: Upload,
              title: t('ftp.uploadFiles'),
              onClick: pickUploadDialog,
            },
            {
              id: 'disconnect',
              Icon: Unplug,
              title: t('ftp.disconnect'),
              danger: true,
              onClick: () => store.disconnect(activeConnId),
            },
          ]}
          onEntryOpen={(entry) => {
            if (entry.isDir) store.listRemote(entry.path).catch(() => {});
            else transferBatch('download', [{ name: entry.name, start: (meta) => startDownload(entry, undefined, meta) }]);
          }}
          buildMenuItems={buildRemoteMenu}
          buildBackgroundMenuItems={buildRemoteBackgroundMenu}
          onDropPane={makeDropHandler('remote')}
          onDropRow={makeRowDropHandler('remote')}
          onDropInto={(folder, e) => makeDropHandler('remote')(e, folder.path)}
          onDeleteSelection={(chosen) => setConfirmDelete({ entries: chosen })}
          onRenameEntry={(entry) => setNameModal({ mode: 'rename', entry })}
          confirmBar={null}
          t={t}
        />
      </div>

      {notice && <div className="ftp-notice">{notice}</div>}

      <TransfersStrip t={t} />

      {nameModal && (
        <NameModal
          spec={nameModal}
          onClose={() => setNameModal(null)}
          onSubmit={async (name) => {
            const store_ = useFtpStore.getState();
            if (nameModal.mode === 'archive') {
              return doArchive(name, nameModal.targets, nameModal.tool);
            }
            if (nameModal.mode === 'move') {
              return moveEntriesTo(resolveRemotePath(remotePath, name), nameModal.targets);
            }
            if (nameModal.mode === 'rename') {
              return store_.renameRemote(
                nameModal.entry.path,
                joinRemote(parentRemote(nameModal.entry.path), name)
              );
            }
            // **A collision check before either create.** `ftp_write_text` with
            // no `expectedMtime` TRUNCATES whatever is at the path, so an
            // unguarded "New file" onto an existing name destroys it without a
            // word. Checked against the listing the user is looking at: no extra
            // round trip, and no probe that tells us about paths they cannot see.
            const taken = await remoteNamesAt(remotePath);
            if (taken.has(name)) {
              const clash = { isDir: taken.get(name) };
              // Say WHICH kind is in the way. A file and a folder cannot share
              // a name in one directory on any filesystem — they are the same
              // directory entry — so "already here" alone reads like a
              // policy of ours rather than a fact, when the fix is an extension.
              const creatingFile = nameModal.mode === 'newfile';
              const key = clash.isDir === creatingFile
                ? clash.isDir
                  ? 'ftp.nameIsFolder'
                  : 'ftp.nameIsFile'
                : 'ftp.nameExists';
              return Promise.reject(new Error(t(key, { name })));
            }
            return nameModal.mode === 'newfile'
              ? store_.createRemoteFile(joinRemote(remotePath, name))
              : store_.mkdirRemote(joinRemote(remotePath, name));
          }}
        />
      )}

      {zipHelp && <ZipInstallModal onClose={() => setZipHelp(false)} />}

      {exportSources && (
        <ExportArchiveModal
          sources={exportSources}
          defaultFolder={localPath}
          onClose={() => setExportSources(null)}
          onDone={(res) => {
            setExportSources(null);
            showNotice(t('ftp.exportDone', { name: res.name, count: res.files }));
            const st = useFtpStore.getState();
            if (res.folder === st.localPath) st.listLocal(st.localPath).catch(() => {});
          }}
        />
      )}

      {comparePick && (
        <RemoteComparePicker
          localName={comparePick.name}
          remotePath={remotePath}
          remoteFiles={remoteEntries.filter((e) => !e.isDir)}
          onClose={() => setComparePick(null)}
          onPick={async (target) => {
            // Read first: a too-large, binary or missing file is reported IN the
            // picker (the throw lands in its error line), not in an editor that
            // opens and then fails.
            const res = await invoke('ftp_read_text', { connId: activeConnId, path: target });
            const local = comparePick;
            setComparePick(null);
            setEditFile({
              ...local,
              local: true,
              compareWith: { label: 'server', name: `${t('ftp.remotePane')}: ${target}`, content: res.content },
            });
          }}
        />
      )}

      {conflictQueue.length > 0 && (
        // One at a time: the head of the queue. `key` remounts per question so
        // focus returns to Skip for each file rather than staying on whatever
        // was clicked for the previous one.
        <TransferConflictModal
          key={`${conflictQueue[0].kind}:${conflictQueue[0].name}:${conflictQueue.length}`}
          conflict={conflictQueue[0]}
          onAnswer={answerConflict}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          entries={confirmDelete.entries}
          onClose={() => setConfirmDelete(null)}
          onConfirm={doDelete}
        />
      )}

      {permEntry && (
        <PermModal
          entry={permEntry}
          onClose={() => setPermEntry(null)}
          onSubmit={(mode) => useFtpStore.getState().chmodRemote(permEntry.path, mode)}
        />
      )}

      {editFile && (
        <FileEditModal
          isOpen
          local={!!editFile.local}
          compareWith={editFile.compareWith || null}
          connId={editFile.local ? null : activeConnId}
          path={editFile.path}
          name={editFile.name}
          onClose={() => setEditFile(null)}
          onSaved={() => {
            // Size/mtime shown in the listing changed — refresh in place.
            const s = useFtpStore.getState();
            s.listRemote(s.getRemotePath()).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
